import { Box, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { TriggerInterruption } from '@mytaverse-shared/interfaces/assistantScript';
import ChoiceCard from '../ChoiceCard';
import {
  InterruptSectionContainer,
  InterruptMenuPauseIcon,
  InterruptMenuStopIcon,
  InterruptMenuLockIcon,
  BridgeGapsBox,
  BridgeGapsTitle,
  BridgeGapsDescription,
} from './styled';

interface InterruptSectionProps {
  interruptMode: TriggerInterruption;
  onInterruptModeChange: (mode: TriggerInterruption) => void;
  bridgeRegionGaps: boolean;
  onBridgeRegionGapsChange: (bridge: boolean) => void;
}

type Option = {
  value: TriggerInterruption;
  icon: JSX.Element;
  label: string;
  desc: string;
  badge?: string;
};

const InterruptSection = ({
  interruptMode,
  onInterruptModeChange,
  bridgeRegionGaps,
  onBridgeRegionGapsChange,
}: InterruptSectionProps) => {
  const { t } = useTranslation('common');

  const options: Option[] = [
    {
      value: 'finish_step',
      icon: <InterruptMenuPauseIcon />,
      label: t('assistant.scriptDialog.interruptFinishStep'),
      desc: t('assistant.scriptDialog.interruptFinishStepDesc'),
      badge: t('assistant.scriptDialog.policies.once_session.badge'),
    },
    {
      value: 'hard_cut',
      icon: <InterruptMenuStopIcon />,
      label: t('assistant.scriptDialog.interruptHardCut'),
      desc: t('assistant.scriptDialog.interruptHardCutDesc'),
    },
    {
      value: 'non_interruptible',
      icon: <InterruptMenuLockIcon />,
      label: t('assistant.scriptDialog.interruptNonInterruptible'),
      desc: t('assistant.scriptDialog.interruptNonInterruptibleDesc'),
    },
  ];

  return (
    <InterruptSectionContainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {options.map((o) => (
          <ChoiceCard
            key={o.value}
            icon={o.icon}
            title={o.label}
            desc={o.desc}
            badge={o.badge}
            selected={interruptMode === o.value}
            onClick={() => onInterruptModeChange(o.value)}
            dataTestId={`assistant-script-interrupt-option-${o.value}`}
          />
        ))}
      </Box>

      {interruptMode === 'finish_step' && (
        <BridgeGapsBox>
          <Switch
            checked={bridgeRegionGaps}
            onChange={(e) => onBridgeRegionGapsChange(e.target.checked)}
            inputProps={
              {
                'data-testid': 'assistant-script-bridge-region-gaps-switch',
              } as any
            }
          />
          <Box>
            <BridgeGapsTitle>
              {t('assistant.scriptDialog.bridgeRegionGaps')}
            </BridgeGapsTitle>
            <BridgeGapsDescription>
              {t('assistant.scriptDialog.bridgeRegionGapsDesc')}
            </BridgeGapsDescription>
          </Box>
        </BridgeGapsBox>
      )}
    </InterruptSectionContainer>
  );
};

export default InterruptSection;
